import { Address } from './address.model';

/**
 * An address as it sits in an order rather than in the address book.
 *
 * A warehouse can be present several times in one order - once per allocation group - so the row is
 * identified by the pair (address, allocation group), not by the address alone. The group is carried
 * here instead of on Address, which stays a pure address book entry: for personal and corporate rows
 * the group is never set.
 */
export type OrderPresetAddress = Address & { allocation_group_id?: number };

/**
 * Copies an address into an order row that owns the given allocation group. The copy is deliberate:
 * two rows of the same warehouse must not share one instance, or they would share one group.
 *
 * The uuid is carried over, because it is the identity of the row: picking a group must not look like
 * a new row to the trackBy of the address list, or the dropdown would be re-created mid-selection.
 * A caller that needs a genuinely new row passes an address with no uuid.
 */
export function withAllocationGroup(address: Address, allocationGroupId: number): OrderPresetAddress {
    if (!address) {
        return address;
    }

    const copy = new Address({...address}) as OrderPresetAddress;
    copy.allocation_group_id = allocationGroupId;

    return copy;
}

/**
 * Copies an address into a row that owns its identity.
 *
 * A row is identified by its uuid, not by its address: the same warehouse can fill two rows - one per
 * allocation group - while the address book hands out a single shared instance for it. Two rows built
 * straight from that instance would carry one uuid and be tracked as a single row.
 *
 * `rowUuid` keeps a row that already exists on the identity it had while its address is replaced, so
 * the row is not re-created underneath the dropdown the user is working in. The allocation group is
 * dropped either way - it belonged to the address that is being replaced.
 */
export function asOwnRow(address: Address, rowUuid?: string): OrderPresetAddress {
    if (!address) {
        return address;
    }

    return new Address({...address, uuid: rowUuid || undefined, allocation_group_id: undefined}) as OrderPresetAddress;
}

/** Reads the allocation group of an order row; undefined for address book entries and non-warehouse rows. */
export function allocationGroupIdOf(address: Address): number | undefined {
    return (address as OrderPresetAddress)?.allocation_group_id;
}

/** What is wrong with the allocation groups of the warehouse rows of an order, if anything. */
export enum AllocationGroupIssue {
    None = 'none',
    /** The user has no allocation groups at all - nothing can be picked, the account team has to set them up. */
    NotConfigured = 'not-configured',
    /** Groups exist, but at least one warehouse row is still without one. */
    NotSelected = 'not-selected',
}

/**
 * A Buy Window order may ship to warehouses, and every warehouse row has to carry an allocation
 * group before it can be saved. Rows of any other type never carry one, so they are ignored here,
 * and an order without warehouse rows has nothing to check.
 */
export function findAllocationGroupIssue(
    addresses: OrderPresetAddress[],
    allocationGroups: { allocation_group_id: number }[],
): AllocationGroupIssue {
    const warehouseRows = (addresses || []).filter(address => address?.isWarehouse);

    if (warehouseRows.length === 0) {
        return AllocationGroupIssue.None;
    }

    if (!allocationGroups || allocationGroups.length === 0) {
        return AllocationGroupIssue.NotConfigured;
    }

    return warehouseRows.some(address => !allocationGroupIdOf(address))
        ? AllocationGroupIssue.NotSelected
        : AllocationGroupIssue.None;
}

/**
 * The allocation groups already spoken for by the other rows, per row index: a group backs one row
 * only, so the dropdown of a row must not offer what another row has taken. The row's own group is
 * kept in its own list, or the control would have nothing to display for the current selection.
 */
export function allocationGroupsTakenByOtherRows(addresses: OrderPresetAddress[]): number[][] {
    const groupIds = (addresses || []).map(address => allocationGroupIdOf(address));

    return groupIds.map((_, index) => groupIds.filter((id, other) => other !== index && !!id));
}
